import { type Media } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import date from "@common/lib/date";
import { getApi } from "@common/modules/data/api";
import { type Data } from "@common/modules/data/types";
import React from "react";
import { type GridConfigColumn } from "../../types";
import { getTotalSaleExtraColumns, totalSaleChartTypes } from "../lib";
import { type ProviderProps, type TotalSaleBase, type TotalSalesBaseRequest } from "../types";
import CommonStaticProvider from "./CommonStaticProvider";

export type TotalSalesByContact = TotalSaleBase & {
  contact: string;
  userId: string;
  profilePictureMediaSourceKey: Media.Source | null;
  profilePictureMediaSourceId: string | null;
};

export type TotalSalesByContactRequest = TotalSalesBaseRequest;
export type Props = Omit<ProviderProps, "id">;
export const SHOP_TOTAL_SALES_BY_CONTACT = "1c01b873-bf73-407b-b539-0cfb3c1ee4d0";

const TotalSalesByContactProvider = (props: Props) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const fields = React.useMemo(
    (): Data.Api.FormField[] => [
      {
        formControlKey: "DateRange",
        formFieldId: "dateRange",
        formFieldKey: "Date",
        label: i18n.dateRange,
        plainTextValue: `{"from": "${date.extractDate(date.addMonth(new Date(), -1))}","to": "${date.extractDate(
          new Date()
        )}"}`
      }
    ],
    []
  );

  const getChartGridData = React.useCallback((id, data) => api.getTotalSalesByContact(data as any), [api]);

  const getColumns = React.useCallback((): GridConfigColumn<TotalSalesByContact>[] => {
    return [
      {
        dataType: "string",
        key: "contact",
        label: i18n.contact
      }
    ];
  }, []);

  const extraColumns = React.useMemo(() => getTotalSaleExtraColumns(i18n), [i18n]);

  return (
    <CommonStaticProvider
      badges={["StartDate"]}
      category="Shop"
      fields={fields}
      isLoading={false}
      onLoaded={props.onLoaded}
    >
      {({ getBadges, getFilters, parameters }) =>
        props.children({
          id: SHOP_TOTAL_SALES_BY_CONTACT,
          chartTypes: totalSaleChartTypes,
          config: {
            aspectRatio: 2.5,
            colors: [],
            legend: "hide",
            xAxis: {
              key: "contact"
            },
            yAxis: [{ key: "totalSales", color: "{$Blue}", label: i18n.totalSales }],
            totalRecords: 25,
            yNoData: "hide"
          },
          extraColumns,
          fields,
          hasGrid: true,
          isLoading: false,
          name: i18n.totalSalesByContact,
          parameters,
          type: "Bar",
          getBadges,
          getChartData: getChartGridData,
          getFilters,
          gridConfig: { columns: [], pageSize: 25, key: "userId" },
          gridColumns: getColumns
        })
      }
    </CommonStaticProvider>
  );
};

export default TotalSalesByContactProvider;
